import { Box, Paper, type PaperProps } from '@mui/material'
import {
    DataGridPro,
    type DataGridProProps,
    type GridColumnGroupingModel,
    type GridColumnVisibilityModel,
    type GridSortModel,
    useGridApiRef,
} from '@mui/x-data-grid-pro'
import { SortIndexField } from '__generated__/graphql'
import {
    CandidateTableFooterSlot,
    CandidateTableToolbarSlot,
} from 'recruiter/components/CandidateTableToolbar'
import {
    FiltersDrawer,
    type FilterTab,
} from 'recruiter/components/FiltersDrawer'
import { ColumnsManagementWithShowAll } from 'shared/components/ColumnsManagementWithShowAll'
import { useLocalStorage } from 'shared/hooks/useLocalStorage'
import { getPxAsRem } from 'shared/utils'
import {
    type CandidateSortField,
    type CandidateSortState,
    type SortState,
} from 'shared/hooks/candidateSearchUrlState'
import {
    type CandidateTableColumnDefinition,
    type CandidateTableRowModel,
} from './candidateTableColumns'
import { NameColumnHeader } from './NameColumnHeader'

export const SORT_INDEX_TO_FIELD: Partial<
    Record<SortIndexField, CandidateSortField>
> = {
    [SortIndexField.LastName]: 'lastName',
    [SortIndexField.FirstName]: 'firstName',
    [SortIndexField.Company]: 'company',
    [SortIndexField.DateOnRoster]: 'dateOnRoster',
    [SortIndexField.ProcessStatus]: 'processStatus',
    [SortIndexField.ProcessStatusUpdatedAt]: 'processStatusUpdatedAt',
    [SortIndexField.SectorDescription]: 'sectorDescription',
    [SortIndexField.CurrentTitle]: 'currentTitle',
    [SortIndexField.CurrentIndustry]: 'currentIndustry',
    [SortIndexField.CurrentSector]: 'currentSector',
    [SortIndexField.CurrentLocation]: 'currentLocation',
    [SortIndexField.StartDate]: 'startDate',
    [SortIndexField.PreviousCompany]: 'previousCompany',
    [SortIndexField.PreviousIndustry]: 'previousIndustry',
    [SortIndexField.PreviousSector]: 'previousSector',
    [SortIndexField.Gender]: 'gender',
    [SortIndexField.Ethnicity]: 'ethnicity',
    [SortIndexField.VisaStatus]: 'visaStatus',
    [SortIndexField.CpiAttribute]: 'cpiAttribute',
    [SortIndexField.UndergradCollege]: 'undergradCollege',
    [SortIndexField.GraduateCollege]: 'graduateCollege',
    [SortIndexField.UndergradYear]: 'undergradYear',
    [SortIndexField.UndergradGpa]: 'undergradGpa',
    [SortIndexField.Sat]: 'sat',
    [SortIndexField.Act]: 'act',
    [SortIndexField.Gmat]: 'gmat',
    [SortIndexField.Hometown]: 'hometown',
    [SortIndexField.AnalystClassYear]: 'analystClassYear',
    [SortIndexField.RecruitingIndustryPref_1]: 'recruitingIndustryPref1',
    [SortIndexField.RecruitingIndustryPref_2]: 'recruitingIndustryPref2',
    [SortIndexField.RecruitingSectorPref_1]: 'recruitingSectorPref1',
    [SortIndexField.RecruitingSectorPref_2]: 'recruitingSectorPref2',
    [SortIndexField.RecruitingGeoPref_1]: 'recruitingGeoPref1',
    [SortIndexField.RecruitingGeoPref_2]: 'recruitingGeoPref2',
    [SortIndexField.StarRatingMostRecentScreen]: 'starRatingMostRecentScreen',
    [SortIndexField.StarRatingReference]: 'starRatingReference',
    [SortIndexField.ReferenceAttribute]: 'referenceAttribute',
    [SortIndexField.PreAdmitAttribute]: 'preAdmitAttribute',
    [SortIndexField.PreferredEmail]: 'preferredEmail',
    [SortIndexField.ResumeUpdated]: 'resumeUpdated',
    [SortIndexField.RosterInternalNote]: 'internalNote',
    [SortIndexField.ClientNote]: 'clientNote',
    [SortIndexField.StarRating]: 'starRating',
    [SortIndexField.Email]: 'email',
    [SortIndexField.Colors]: 'colors',
    [SortIndexField.WorkEmail]: 'workEmail',
}

export const FIELD_TO_SORT_INDEX = Object.fromEntries(
    Object.entries(SORT_INDEX_TO_FIELD).map(([k, v]) => [
        v,
        k as SortIndexField,
    ])
) as Partial<Record<CandidateSortField, SortIndexField>>

export type { CandidateTableRowModel }

interface CandidateSearchResultsTableProps {
    columns: CandidateTableColumnDefinition[]
    data: CandidateTableRowModel[]
    loading: boolean
    sort: SortState
    selectedCandidateIds: string[]
    onSortChange: (sort: SortState) => void
    onSelectionChange: (ids: string[]) => void
    actionButtons?: React.ReactNode
    afterSearchContent?: React.ReactNode
    filterTabs: FilterTab[]
    endContent?: React.ReactNode
    footerStartContent?: React.ReactNode
    containerProps?: PaperProps
    pinnedColumns?: { left?: string[]; right?: string[] }
    columnGroupingModel?: GridColumnGroupingModel
}

export function CandidateSearchResultsTable({
    columns: columnsProp,
    data,
    loading,
    sort,
    selectedCandidateIds,
    onSortChange,
    onSelectionChange,
    actionButtons,
    afterSearchContent,
    filterTabs,
    endContent,
    footerStartContent,
    containerProps,
    pinnedColumns,
    columnGroupingModel,
}: CandidateSearchResultsTableProps) {
    const apiRef = useGridApiRef()
    const [columnVisibilityModel, setColumnVisibilityModel] =
        useLocalStorage<GridColumnVisibilityModel>(
            'research-candidates-columns',
            { rosterCandidateFavoriteCount: false, systemStatus: false }
        )
    const [columnWidths, setColumnWidths] = useLocalStorage<
        Record<string, number>
    >('candidate-table-column-widths', {})

    const columns = columnsProp.map((col) => {
        const storedWidth = columnWidths[col.field]
        const withWidth =
            storedWidth != null
                ? { ...col, width: storedWidth, flex: undefined }
                : col
        return col.field === 'candidate'
            ? {
                  ...withWidth,
                  renderHeader: () => (
                      <NameColumnHeader
                          sort={sort}
                          onSortChange={onSortChange}
                      />
                  ),
              }
            : withWidth
    })

    const sortModel: GridSortModel = sort
        .map((s) => {
            if (s.referenceRosterId) {
                const col = columnsProp.find(
                    (c) =>
                        c.referenceRosterId === s.referenceRosterId &&
                        c.referenceSortField === s.field
                )
                return col ? { field: col.field, sort: s.order } : null
            }
            const sortIndexField = FIELD_TO_SORT_INDEX[s.field]
            if (!sortIndexField) return null
            const col = columnsProp.find(
                (c) => c.sortIndexField === sortIndexField
            )
            return col ? { field: col.field, sort: s.order } : null
        })
        .filter((item): item is NonNullable<typeof item> => item !== null)

    const handleSortModelChange: DataGridProProps['onSortModelChange'] = (
        model
    ) => {
        if (model.length === 0) {
            onSortChange([])
            return
        }

        const newSort = model
            .map((gridSort): CandidateSortState | null => {
                const col = columnsProp.find((c) => c.field === gridSort.field)
                if (!col || !gridSort.sort) return null
                if (col.referenceRosterId && col.referenceSortField) {
                    return {
                        field: col.referenceSortField,
                        order: gridSort.sort,
                        referenceRosterId: col.referenceRosterId,
                    }
                }
                if (!col.sortIndexField) return null
                const mappedField = SORT_INDEX_TO_FIELD[col.sortIndexField]
                if (!mappedField) return null
                return { field: mappedField, order: gridSort.sort }
            })
            .filter((s): s is CandidateSortState => s !== null)

        onSortChange(newSort)
    }

    return (
        <Paper
            elevation={0}
            {...containerProps}
            sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                paddingX: getPxAsRem(20),
                paddingTop: getPxAsRem(16),
                ...containerProps?.sx,
            }}
        >
            <CandidateTableToolbarSlot
                selectedCount={selectedCandidateIds.length}
                actionButtons={actionButtons}
                afterSearchContent={afterSearchContent}
                endContent={endContent}
            />
            <Box
                sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'row',
                    overflow: 'hidden',
                }}
            >
                <FiltersDrawer tabs={filterTabs} />
                <DataGridPro
                    apiRef={apiRef}
                    rows={data}
                    columns={columns}
                    columnGroupingModel={columnGroupingModel}
                    getRowId={(row) => row.candidateId ?? row.id}
                    pinnedColumns={pinnedColumns}
                    columnVisibilityModel={columnVisibilityModel}
                    onColumnVisibilityModelChange={setColumnVisibilityModel}
                    onColumnWidthChange={(params) => {
                        setColumnWidths((prev) => ({
                            ...prev,
                            [params.colDef.field]: params.width,
                        }))
                    }}
                    checkboxSelection
                    keepNonExistentRowsSelected
                    rowSelectionModel={selectedCandidateIds}
                    onRowSelectionModelChange={(model) => {
                        onSelectionChange(
                            Array.from(model).map((id) => id.toString())
                        )
                    }}
                    sortingMode="server"
                    sortModel={sortModel}
                    onSortModelChange={handleSortModelChange}
                    disableMultipleColumnsSorting
                    loading={loading}
                    rowHeight={41}
                    columnHeaderHeight={56}
                    disableColumnMenu
                    disableRowSelectionOnClick
                    disableColumnReorder
                    sx={{
                        flex: 1,
                        border: 0,
                        '& .MuiDataGrid-cell': {
                            fontSize: '0.8125rem',
                            borderTop: 'none',
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            display: 'flex',
                            alignItems: 'center',
                        },
                        '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within':
                            {
                                outline: 'none',
                            },
                        '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within':
                            {
                                outline: 'none',
                            },
                        '& .MuiDataGrid-cell[data-colindex="0"], & .MuiDataGrid-columnHeader[aria-colindex="1"]':
                            {
                                pl: 2,
                            },
                        '& .MuiDataGrid-filler > div': {
                            borderTop: 'none',
                        },
                        '& .MuiDataGrid-footerContainer': {
                            pl: 2,
                        },
                        '& .MuiCheckbox-root': {
                            padding: '4px',
                            '& .MuiSvgIcon-root': {
                                fontSize: '1.125rem',
                            },
                        },
                        '& .MuiDataGrid-cellCheckbox, & .MuiDataGrid-columnHeaderCheckbox':
                            {
                                minWidth: '48px !important',
                                maxWidth: '48px !important',
                                width: '48px !important',
                                justifyContent: 'center',
                                pl: '0 !important',
                            },
                    }}
                    slotProps={{
                        panel: {
                            sx: {
                                '& .MuiDataGrid-columnsManagementHeader': {
                                    display: 'none',
                                },
                                '& .MuiDataGrid-columnsManagement': {
                                    padding: 1,
                                },
                                '& .MuiDataGrid-columnsManagementFooter': {
                                    paddingLeft: 1,
                                    '& > label': {
                                        marginLeft: 0,
                                    },
                                },
                            },
                        },
                        columnsPanel: {
                            sx: {
                                '& .MuiDataGrid-columnsManagementRow': {
                                    py: 0.25,
                                    marginLeft: 0,
                                },
                                '& .MuiFormControlLabel-label': {
                                    fontSize: '0.75rem',
                                },
                                '& .MuiCheckbox-root': {
                                    transform: 'scale(0.8)',
                                },
                                '& .MuiButton-root': {
                                    fontSize: '0.6875rem',
                                    textTransform: 'none',
                                    py: 0.5,
                                    px: 1,
                                    minWidth: 'auto',
                                },
                            },
                        },
                    }}
                    slots={{
                        toolbar: () => null,
                        footer: () => (
                            <CandidateTableFooterSlot
                                startContent={footerStartContent}
                            />
                        ),
                        columnsManagement: ColumnsManagementWithShowAll,
                    }}
                />
            </Box>
        </Paper>
    )
}
